import { normalizeState, orderDateValue, formatDate, todayParis } from "./commercial.mjs";
import { initialState } from "./repository.mjs";

const referenceId = (value) =>
  String(value?._id ?? value?.id ?? value ?? "").trim();

function orderKey(order) {
  const id = referenceId(order._id || order.id);
  if (id) return `id:${id}`;
  return [
    orderDateValue(order),
    order.typeContrat || order.contractType,
    order.qualification || order.task,
    order.quantity,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .join("|");
}

function mergeOrders(savedOrders = [], apiOrders = []) {
  const unique = new Map();
  for (const order of savedOrders) {
    if (!order?.apiOrder) unique.set(orderKey(order), order);
  }
  for (const order of apiOrders) {
    const id = referenceId(order._id || order.id);
    unique.set(orderKey(order), {
      ...order,
      id: id || order.id,
      date: orderDateValue(order),
      apiOrder: true,
    });
  }
  return [...unique.values()];
}

const timeParis = (value) =>
  new Date(value).toLocaleTimeString("fr-FR", { timeZone: "Europe/Paris", hour: "2-digit", minute: "2-digit" });
// The history of an establishment is the list of its commercial events.
export function historyFromEvents(events = [], clientId) {
  return events
    .filter((event) => referenceId(event.clientId) === clientId && event.label)
    .sort((a, b) => String(a.createdAt || "").localeCompare(String(b.createdAt || "")))
    .map((event) =>
      event.type === "history" || !Number.isFinite(Date.parse(event.createdAt))
        ? event.label
        : `${formatDate(todayParis(new Date(event.createdAt)))} ${timeParis(event.createdAt)} — ${event.label}`,
    );
}

export function connectClients(clients, user, saved, orders = []) {
  const previous = saved?.clients || [];
  const ordersByClient = new Map();
  for (const order of Array.isArray(orders) ? orders : []) {
    const clientId = referenceId(order.clientId || order.client);
    if (!clientId) continue;
    ordersByClient.set(clientId, [
      ...(ordersByClient.get(clientId) || []),
      order,
    ]);
  }
  const mapped = clients.map(client => {
    const clientId = referenceId(client._id || client.id);
    const existing = client.commercialFollowUp || previous.find(
      row =>
        referenceId(row.establishment_id) === clientId ||
        referenceId(row.id) === clientId,
    );
    const contacts = (client.contacts || []).map((contact, index) => ({
      ...contact, id: String(contact.id || contact._id || `${client._id}-contact-${index}`),
      name: [contact.firstname, contact.lastname].filter(Boolean).join(" "),
      firstName: contact.firstname || "", lastName: contact.lastname || "",
      role: contact.profession || "", phone: contact.mobile || contact.phone || "",
      email: contact.email || "", active: contact.active !== false, primary: contact.primary ?? index === 0,
    }));
    if (!contacts.length && (client.contactFirstname || client.contactLastname || client.contactPhone || client.contactEMail)) contacts.push({id:`${client._id}-contact`,name:[client.contactFirstname,client.contactLastname].filter(Boolean).join(" "),phone:client.contactPhone || "",email:client.contactEMail || "",active:true,primary:true});
    const hasPld = client.pld != null && String(client.pld).trim() !== "";
    const clientOrders = mergeOrders(
      existing?.orders,
      ordersByClient.get(clientId),
    );
    const hasOrderHistory = clientOrders.some(order => order.date);
    const isClient = hasPld || hasOrderHistory;
    const previousStatus =
      existing?.status === "PROSPECT" && hasOrderHistory
        ? "CLIENT ACTIF"
        : existing?.status;
    return {
      ...existing,
      prospectingExcluded: client.prospectingBlocked || existing?.prospectingExcluded || false,
      exclusionReason: client.prospectingBlockReason || existing?.exclusionReason || "",
      commercialStatus: client.prospectingBlocked ? "Exclu" : existing?.commercialStatus,
      apiClient: true, id: clientId, establishment_id: clientId, client_id: clientId,
      inactivityThresholds:client.commercialInactivityThresholds||existing?.inactivityThresholds,
      commercialPurgedAt:client.commercialPurgedAt,
      name: client.companyName || "Entreprise sans nom", siret: client.siret || "",
      address: client.address || "", postalCode: client.postCode || "", city: client.town || "",
      phone: client.contactPhone || "", email: client.contactEMail || "",
      kind: isClient ? "client" : "prospect", status: previousStatus || (isClient ? "CLIENT ACTIF" : "PROSPECT"),
      agency: user.agency, ownerId: user.id, owner: user.name,
      contacts: existing?.contacts?.length ? existing.contacts : contacts,
      orders: clientOrders,
      ...(!previous.length ? { history: historyFromEvents(saved?.events, clientId) } : {}),
    };
  });
  // Preserve locally imported prospects; never load the prototype's demo database.
  const imported = previous.filter(row => !row.apiClient && !/^[a-f0-9]{24}$/i.test(referenceId(row.client_id || row.establishment_id || row.id)) && !mapped.some(client => referenceId(client.id) === referenceId(row.id)));
  return normalizeState({ ...(saved || initialState([])), clients: [...mapped, ...imported] });
}
